import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  createUserReplyTemplateThunk,
  deleteUserReplyTemplateThunk,
  updateUserReplyTemplateThunk,
  recoverUserReplyTemplateThunk,
  getReplyTemplatesThunk,
} from '../../thunks/replyTemplates';
import { ERequestApiStatus } from '../../types/appState';
import { EReplyTemplateModalStep, type TReplyTemplatesState } from '../../types/replyTemplates';

import type { SliceActions } from '../types';

export const initialState: TReplyTemplatesState = {
  data: {
    status: ERequestApiStatus.Initial,
    limit: 0,
    replyTemplates: [],
    errorMessage: undefined,
  },
  editableReplyTemplateId: null,
  isNewEnabled: false,
  isEnabled: false,
  isOnboardingEnabled: true,
  isInputButtonVisible: true,
  isModalOpen: false,
  modalStep: null,
  initialText: null,
};

const replyTemplatesSlice = createSlice({
  name: 'replyTemplates',
  reducers: {
    openModal: state => {
      state.isModalOpen = true;
    },
    closeModal: state => {
      state.isModalOpen = false;
      state.modalStep = null;
      state.data.errorMessage = undefined;
      state.data.status = ERequestApiStatus.Initial;
      state.initialText = null;
    },
    setModalStep: (state, action: PayloadAction<EReplyTemplateModalStep>) => {
      state.modalStep = action.payload;
      state.data.errorMessage = undefined;
      state.data.status = ERequestApiStatus.Initial;
    },
    setEditableReplyTemplateId: (state, action: PayloadAction<number | null>) => {
      state.editableReplyTemplateId = action.payload;
    },
    setIsReplyTemplateInputVisible: (state, action: PayloadAction<boolean>) => {
      state.isInputButtonVisible = action.payload;
    },
    setIsReplyTemplateNewEnabled: (state, action: PayloadAction<boolean>) => {
      state.isNewEnabled = action.payload;
    },
    setIsReplyTemplateOnboardingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isOnboardingEnabled = action.payload;
    },
    setReplyTemplateInitialText: (state, action: PayloadAction<string>) => {
      state.initialText = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // getReplyTemplatesThunk
      .addCase(getReplyTemplatesThunk.pending, state => {
        state.data.status = ERequestApiStatus.Pending;
        state.data.errorMessage = undefined;
      })
      .addCase(getReplyTemplatesThunk.fulfilled, (state, action) => {
        state.data.status = ERequestApiStatus.Succeed;
        state.data.replyTemplates = action.payload.replyTemplates;
        state.data.limit = action.payload.limit;
      })
      .addCase(getReplyTemplatesThunk.rejected, (state, action) => {
        state.data.status = ERequestApiStatus.Failed;
        state.data.errorMessage = action.payload || 'Ошибка получения списка шаблонов ответов';
      })
      // createUserReplyTemplateThunk
      .addCase(createUserReplyTemplateThunk.pending, state => {
        state.data.status = ERequestApiStatus.Pending;
        state.data.errorMessage = undefined;
      })
      .addCase(createUserReplyTemplateThunk.fulfilled, (state, action) => {
        state.data.status = ERequestApiStatus.Succeed;
        state.data.replyTemplates = action.payload.replyTemplates;
        state.data.limit = action.payload.limit;
        state.modalStep = EReplyTemplateModalStep.List;
        state.initialText = null;
      })
      .addCase(createUserReplyTemplateThunk.rejected, (state, action) => {
        state.data.status = ERequestApiStatus.Failed;
        state.data.errorMessage = action.payload || 'Ошибка создания шаблона ответа';
      })
      // updateUserReplyTemplateThunk
      .addCase(updateUserReplyTemplateThunk.pending, state => {
        state.data.status = ERequestApiStatus.Pending;
        state.data.errorMessage = undefined;
      })
      .addCase(updateUserReplyTemplateThunk.fulfilled, (state, action) => {
        state.data.status = ERequestApiStatus.Succeed;
        const updatedTemplate = action.payload.template;
        state.data.replyTemplates = state.data.replyTemplates.map(template =>
          template.id === updatedTemplate.id ? updatedTemplate : template,
        );
        state.modalStep = EReplyTemplateModalStep.List;
        state.editableReplyTemplateId = null;
      })
      .addCase(updateUserReplyTemplateThunk.rejected, (state, action) => {
        state.data.status = ERequestApiStatus.Failed;
        state.data.errorMessage = action.payload || 'Ошибка обновления шаблона ответа';
      })
      // recoverUserReplyTemplateThunk
      .addCase(recoverUserReplyTemplateThunk.pending, state => {
        state.data.status = ERequestApiStatus.Pending;
        state.data.errorMessage = undefined;
      })
      .addCase(recoverUserReplyTemplateThunk.fulfilled, (state, action) => {
        state.data.status = ERequestApiStatus.Succeed;
        state.data.replyTemplates = action.payload.replyTemplates;
        state.data.limit = action.payload.limit;
      })
      .addCase(recoverUserReplyTemplateThunk.rejected, (state, action) => {
        state.data.status = ERequestApiStatus.Failed;
        state.data.errorMessage = action.payload || 'Ошибка восстановления шаблона ответа';
      })
      // deleteUserReplyTemplateThunk
      .addCase(deleteUserReplyTemplateThunk.pending, state => {
        state.data.status = ERequestApiStatus.Pending;
        state.data.errorMessage = undefined;
      })
      .addCase(deleteUserReplyTemplateThunk.fulfilled, (state, action) => {
        state.data.status = ERequestApiStatus.Succeed;
        state.data.replyTemplates = state.data.replyTemplates.filter(template => template.id !== action.payload);
      })
      .addCase(deleteUserReplyTemplateThunk.rejected, (state, action) => {
        state.data.status = ERequestApiStatus.Failed;
        state.data.errorMessage = action.payload || 'Ошибка удаления шаблона ответа';
      });
  },
  selectors: {
    selectEditableReplyTemplateId: (state: TReplyTemplatesState) => state.editableReplyTemplateId,
    selectReplyTemplatesList: (state: TReplyTemplatesState) => state.data.replyTemplates,
    selectReplyTemplateLimit: (state: TReplyTemplatesState) => state.data.limit,
    selectReplyTemplateStatus: (state: TReplyTemplatesState) => state.data.status,
    selectReplyTemplateErrorMessage: (state: TReplyTemplatesState) => state.data.errorMessage,
    selectReplyTemplateModalStep: (state: TReplyTemplatesState) => state.modalStep,
    selectReplyTemplateIsModalOpen: (state: TReplyTemplatesState) => state.isModalOpen,
    selectIsReplyTemplateNewEnabled: (state: TReplyTemplatesState) => state.isNewEnabled,
    selectIsReplyTemplateEnabled: (state: TReplyTemplatesState) => state.isEnabled,
    selectIsReplyTemplateInputVisible: (state: TReplyTemplatesState) =>
      Boolean(state.isInputButtonVisible && state.data.replyTemplates?.length),
    selectReplyTemplateInitialText: (state: TReplyTemplatesState) => state.initialText,
  },
  initialState,
});

export const replyTemplatesReducer = replyTemplatesSlice.reducer;
export const {
  openModal,
  closeModal,
  setModalStep,
  setEditableReplyTemplateId,
  setIsReplyTemplateInputVisible,
  setIsReplyTemplateNewEnabled,
  setIsReplyTemplateOnboardingEnabled,
  setReplyTemplateInitialText,
} = replyTemplatesSlice.actions;
export const {
  selectEditableReplyTemplateId,
  selectReplyTemplatesList,
  selectReplyTemplateLimit,
  selectReplyTemplateModalStep,
  selectReplyTemplateIsModalOpen,
  selectIsReplyTemplateNewEnabled,
  selectIsReplyTemplateEnabled,
  selectIsReplyTemplateInputVisible,
  selectReplyTemplateStatus,
  selectReplyTemplateErrorMessage,
  selectReplyTemplateInitialText,
} = replyTemplatesSlice.selectors;

export type TReplyTemplatesActions = SliceActions<typeof replyTemplatesSlice.actions>;
